import { Observable, UnaryFunction } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export function assert(condition: any, msg?: string): asserts condition {
  if (!condition) {
    throw new Error(msg);
  }
}

export function mapNull<T, U>(f: (t: T) => U, x: T | null): U | null {
  if (x == null) return null;
  return f(x);
}

export function mapUndefined<T, U>(
  f: (t: T) => U,
  x: T | undefined
): U | undefined {
  if (x == undefined) return undefined;
  return f(x);
}

export function undefinedToNull<T>(x: T | undefined): T | null {
  return x == undefined ? null : x;
}

export function notNull<T>(x: T | null): x is T {
  return x != null;
}

export function choose<T, U>(
  f: (x: T) => U | null
): UnaryFunction<Observable<T>, Observable<U>> {
  return (o$) => o$.pipe(map(f), filter(notNull));
}

export function assertNotNull<T>(
  msg?: string
): UnaryFunction<Observable<T | null>, Observable<T>> {
  return (o$) =>
    o$.pipe(
      choose((x) => {
        assert(x != null, msg);
        return x;
      })
    );
}

export function ofType<T>(
  f: (x: any) => x is T
): UnaryFunction<Observable<any>, Observable<T>> {
  return (o$) => o$.pipe(choose((x) => (f(x) ? x : null)));
}

export function instanceOf<T>(constructor: {
  new (...args: any[]): T;
}): UnaryFunction<Observable<any>, Observable<T>> {
  return ofType((x: any): x is T => x instanceof constructor);
}

export function nullToDefault<T, U>(value: T | null, someDefault: U): T | U {
  return value == null ? someDefault : value;
}

export function undefinedToDefault<T, U>(
  value: T | undefined,
  someDefault: U
): T | U {
  return value == undefined ? someDefault : value;
}

export interface IndexSignature<T> {
  [key: string]: T;
}

export function nonEmptyString(s: string | null | undefined): boolean {
  return s != null && s != undefined && s != '';
}

declare global {
  interface Array<T> {
    groupBy<K>(selector: (x: T) => K): Map<K, T[]>;
  }
}

declare global {
  interface Map<K, V> {
    reduce<K, V, T>(f: (r: T, k: K, x: V) => T, r: T): T;
  }
}

Array.prototype.groupBy = function <T, K>(
  this: Array<T>,
  selector: (x: T) => K
): Map<K, T[]> {
  return this.reduce((r: Map<K, T[]>, x: T): Map<K, T[]> => {
    const k: K = selector(x);
    const xs: T[] = r.get(k) ?? [];
    xs.push(x);
    return r.set(k, xs);
  }, new Map<K, T[]>());
};

Map.prototype.reduce = function <K, V, T>(
  this: Map<K, V>,
  f: (r: T, k: K, x: V) => T,
  r: T
): T {
  this.forEach((v, k) => {
    r = f(r, k, v);
  });
  return r;
};

// This function performs a deep copy of all properties on an object. This is useful when
// you want to create a true duplicate of a javascript object without the prototype behaviour
// inherited when using { ... <object> } or Object.create(). Those two methods can lead to the
// original being altered when properties on the copy are changed.
export function deepCopy(obj: Object): Object {
  // Handle the 3 simple types, and null or undefined
  if (null == obj || 'object' != typeof obj) return obj;

  // Handle Date
  if (obj instanceof Date) {
    let copy = new Date();
    copy.setTime(obj.getTime());
    return copy;
  }

  // Handle Array
  if (obj instanceof Array) {
    let copy = [];
    for (let i = 0, len = obj.length; i < len; i++) {
      copy.push(deepCopy(obj[i]));
    }
    return copy;
  }

  // Handle Object
  if (obj instanceof Object) {
    let copy: any = {};
    for (let attr in obj) {
      if (obj.hasOwnProperty(attr)) {
        copy[attr] = deepCopy((obj as any)[attr]);
      }
    }
    return copy;
  }

  // Return obj if none of the above criteria are met.
  return obj;
}

export function objectWithoutKey(
  object: { [x: string]: Object },
  key: string
): Object {
  const { [key]: deletedKey, ...otherKeys } = object;
  return otherKeys;
}
